'use client';

import { PAYMENT_STATUS, STORAGE_KEY } from '@/constants';
import { useLocalStorage } from '@/hooks';
import { useGetVendorId } from '@/hooks/useBot';
import { createAskIotUser, getAskIotUserDetails } from '@/services/user';
import { IAskIOTUserDetails, IVendorVendorConfig } from '@/types/user';
import { isString } from 'lodash';
import * as React from 'react';

interface IUserProviderProps {
  children: React.ReactNode;
}
interface IUserContext {
  askIOTUserDetails: IAskIOTUserDetails | null;
  setAskIOTUserIsValid: React.Dispatch<React.SetStateAction<boolean>>;
  isNoPaymentStatus?: boolean;
  isShowRestrictBanner?: boolean;
  vendorConfig?: IVendorVendorConfig;
}

const UserContext = React.createContext<IUserContext>({} as IUserContext);

export const useUserContext = () => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('AuthContext must be used within provider!');
  }
  return context;
};

const UserProvider: React.FunctionComponent<IUserProviderProps> = (props) => {
  const [accessToken] = useLocalStorage(STORAGE_KEY.ACCESS_TOKEN);
  const [askIOTUserDetails, setAskIOTUserDetails] = React.useState<IAskIOTUserDetails | null>(null);
  const [getAskIOTUserIsValid, setAskIOTUserIsValid] = React.useState(true);
  const { data: vendorConfig } = useGetVendorId(!!accessToken);
  // Always allow free access - no payment restrictions
  const isNoPaymentStatus = false;

  /**
   * Handles user authentication flow: retrieve existing user or create new user
   * 
   * This function performs the following operations:
   * 1. Validates that accessToken is available (prevents race conditions)
   * 2. Attempts to fetch existing user details from API
   * 3. If no user exists, creates a new user
   * 4. Updates the askIOTUserDetails state with the result
   * 
   * @critical This function is essential for dashboard loading - if askIOTUserDetails
   * remains null, the dashboard will show infinite loading state
   */
  const userFlow = async () => {
    if (!accessToken) return;

    const userData = await getAskIotUserDetails();
    if (userData === null) {
      const createUser = await createAskIotUser();
      if (createUser) {
        setAskIOTUserDetails(createUser);
      }
    } else {
      setAskIOTUserDetails(userData);
    }
  };

  /**
   * CRITICAL FIX: Handle user authentication flow with proper dependency management
   * 
   * Previous Issue: userFlow() was called immediately on mount before accessToken 
   * was hydrated from localStorage, causing askIOTUserDetails to remain null forever
   * and blocking dashboard loading.
   * 
   * Solution: Wait for both conditions:
   * 1. getAskIOTUserIsValid is true (initial mount or OTP verification)
   * 2. accessToken is available (hydrated from localStorage)
   * 
   * This ensures userFlow() only executes when authentication is properly initialized.
   */
  React.useEffect(() => {
    if (getAskIOTUserIsValid && accessToken) {
      userFlow();
      setAskIOTUserIsValid(false);
    }
  }, [getAskIOTUserIsValid, accessToken]);

  return (
    <UserContext.Provider
      value={{
        askIOTUserDetails,
        setAskIOTUserIsValid,
        isNoPaymentStatus,
        isShowRestrictBanner:
          isString(askIOTUserDetails?.subscriptionplan) &&
          askIOTUserDetails?.subscriptionplan?.toLowerCase() !== 'enterprise',
        vendorConfig,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export default UserProvider;
